import React, {useEffect, useRef, useState} from 'react';

type WorkspaceDrawerScrollerProps = React.ComponentPropsWithRef<'div'> & {
  /**
   * If there's a footer element displayed after this scroller, specify
   * the footer's height to subtract it from the scrollable height
   */
  footerHeight?: number;
};

/**
 * A scrollable container with dynamic height that
 * grows and shrinks with the workspace drawer.
 *
 * NOTE: the height of the workspace drawer depends on the
 * user's scroll position on the page. At the top of the
 * workspace, the drawer is rendered below the navbar and
 * viewbar. As the user scrolls down, the drawer grows taller
 * until it sticks to the top and occupies the full view height.
 */
export const WorkspaceDrawerScroller = React.forwardRef<
  HTMLDivElement,
  WorkspaceDrawerScrollerProps
>(({footerHeight = 0, style, ...props}, forwardedRef) => {
  // For more context on why an innerRef is needed in addition to
  // the forwardedRef, see: https://stackoverflow.com/a/62238917
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState('');
  useEffect(() => {
    const updateHeight = () => {
      if (innerRef.current) {
        const top = Math.ceil(innerRef.current.getBoundingClientRect().top);
        setHeight(`calc(100vh - ${top + footerHeight}px)`);
      }
    };
    updateHeight();
    window.addEventListener('scroll', updateHeight);
    window.addEventListener('resize', updateHeight);
    return () => {
      window.removeEventListener('scroll', updateHeight);
      window.removeEventListener('resize', updateHeight);
    };
  }, [footerHeight]);
  return (
    <div
      ref={node => {
        innerRef.current = node;
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
      }}
      style={{...style, height, overflowY: 'auto'}}
      {...props}
    />
  );
});
